<script>
import * as m from '@app/paraglide/messages.js';
import {
    modalState, hideModal
} from '@app/store/modalStore.svelte.js';
import { toast } from '@zerodevx/svelte-toast';
import {
    eventGroupState
} from '@app/store/eventGroupStore.svelte.js';
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import {
    editorState
} from "@app/store/editorStore.svelte.js";
import {
    insertHistoryAction
} from '@app/store/historyStore.svelte.js';
import {
    fade
} from 'svelte/transition';

/* CC LIB */
import voiceUtils from '@cclib/providers/textToSpeechAll/voiceUtils.js';

import { assignEventAudio } from '@app/lib/audio/assignEventAudio.js';
import { renderEventTts } from '@app/lib/audio/renderEventAudio.js';
import { eventNeedsRender } from '@app/lib/audio/audioRenderState.js';
import {
    refreshEventAudioUrl,
    getEventAudioStoragePath,
} from '@app/lib/audio/refreshEventAudioUrl.js';
import { isAudioGroupType } from '@app/lib/eventGroupTypes.js';
import {
    canFitEvent,
    computeResidualSpeed,
} from '@app/lib/audio/fitEventToDuration.js';

/* Fit mode: render at TTS rate 1, then cover any overrun with playback speed */
const fitToDuration = Boolean(modalState.context?.fitToDuration);

let progress = $state(0),
    statusMsg = $state(m.render_audio_starting_status()),
    queueSize = 10,
    eventQueue,
    selectedEventCache = [...eventGroupState[projectState.selected].selected],
    selectedEventIds = eventGroupState[projectState.selected].events
        .filter((event, index) => selectedEventCache.indexOf(index) > -1)
        .map((event) => event.id),
    totalEvents = $state(selectedEventCache.length);

function getSelectedLiveEvents() {
    return eventGroupState[projectState.selected].events.filter((event) => selectedEventIds.includes(event.id));
}

async function beginProcessing() {
    toast.push(m.render_audio_converting_toast(), {classes: ['toast-info']});

    if (eventGroupState[projectState.selected] && !isAudioGroupType(eventGroupState[projectState.selected].type)) {
        toast.push(m.render_audio_must_be_ad_toast(), {classes: ['toast-danger']});
        hideModal();
        return;
    }

    try {
        eventGroupState[projectState.selected].selected = [];

        if (fitToDuration) {
            getSelectedLiveEvents().forEach((event) => {
                if (canFitEvent(event)) event.speed = 1;
            });
        }

        eventQueue = JSON.parse(JSON.stringify(getSelectedLiveEvents()));

        statusMsg = m.render_audio_processing_queue_status();
        processQueue();
    } catch (err) {
        console.log(err, err.message);
        toast.push(m.render_audio_failed_toast({error: err.message}), {classes: ['toast-danger']});
        hideModal();
    }
}

function processQueue() {
    if (eventQueue.length === 0) {
        onPassComplete();
        return;
    }

    let elevenLabsEventIndex = eventQueue.findIndex((evt) => {
        return evt.voice.provider === "ElevenLabs"
    });

    const batchSize = elevenLabsEventIndex > -1 ? 2 : queueSize;
    renderAudio(eventQueue.splice(0, Math.min(batchSize, eventQueue.length)));
}

function onPassComplete() {
    if (fitToDuration) {
        getSelectedLiveEvents().forEach((event) => {
            if (canFitEvent(event)) event.speed = computeResidualSpeed(event);
        });
    }

    finishProcess();
}

function renderAudio(queue) {
    try {
        progress = (((totalEvents - eventQueue.length) / totalEvents) * 100).toFixed(2);
        statusMsg = m.render_audio_rendering_status({start: totalEvents-eventQueue.length, end: Math.min(totalEvents-eventQueue.length+(queueSize-1), totalEvents), total: totalEvents});
        let renderTasks = [];
        
        queue.forEach((event) => {
            const isManualRecord = event.voice?.name === "Manual Record";
            const liveEvent = eventGroupState[projectState.selected].events.find(
                (live) => live.id === event.id,
            );
            const storagePath = getEventAudioStoragePath(liveEvent || event);
            // Refresh a stored object when text/voice still match. Otherwise
            // synthesize so a stale path cannot hide a mismatch. Manual Record
            // events cannot be synthesized; they stay flagged until re-recorded.
            if (!eventNeedsRender(event) && storagePath && liveEvent) {
                renderTasks.push(
                    refreshEventAudioUrl(liveEvent).then((ok) => {
                        if (ok) {
                            return {
                                data: {
                                    eventId: event.id,
                                    isRefresh: true,
                                },
                            };
                        }
                        if (isManualRecord) return null;
                        return renderEventTts(event, eventGroupState[projectState.selected].id).then((result) => {
                            if (!result) return null;
                            return {
                                data: {
                                    eventId: result.eventId,
                                    audio: result.audio,
                                    durationSec: result.durationSec,
                                    storagePath: result.storagePath,
                                },
                            };
                        });
                    })
                );
            } else if (!isManualRecord) {
                renderTasks.push(
                    renderEventTts(event, eventGroupState[projectState.selected].id).then((result) => {
                        if (!result) return null;
                        return {
                            data: {
                                eventId: result.eventId,
                                audio: result.audio,
                                durationSec: result.durationSec,
                                storagePath: result.storagePath,
                            }
                        };
                    })
                );
            }
        });

        Promise.allSettled(renderTasks).then((results) => {
            results.forEach(res => {
                if (res.status === "rejected") {
                    console.log(res.reason);
                    toast.push(m.render_audio_failed_toast({error: res.reason?.message || res.reason}), {classes: ['toast-danger']});
                    return;
                }

                if (!res.value) {
                    return;
                }

                console.log(res);

                let eventIndex = eventGroupState[projectState.selected].events.findIndex(event => {
                    return event.id === res.value.data.eventId;
                })

                const targetEvent = eventGroupState[projectState.selected].events[eventIndex];

                if (res.value.data.isRefresh) {
                    return;
                }

                const durationSec = res.value.data.durationSec;

                assignEventAudio(targetEvent, {
                    audioFile: res.value.data.audio,
                    audioFileDuration: durationSec,
                    audioFileRemote: res.value.data.storagePath,
                });

                /* Fit mode keeps the event timing fixed and adapts the audio instead */
                if (!fitToDuration && editorState.autoTrimEvent && !isNaN(targetEvent.start) && durationSec > 0){
                    targetEvent.end = targetEvent.start + voiceUtils.effectiveAudioDuration(targetEvent) + 0.5;
                }
            });

            processQueue();
        }).catch(err => {
            console.log(err, err.message);
            toast.push(m.render_audio_failed_retry_toast({error: err.message}), {classes: ['toast-danger']});
            hideModal();
        });
    } catch (err) {
        console.log(err, err.message);
        toast.push(m.render_audio_failed_toast({error: err.message}), {classes: ['toast-danger']});
        hideModal();
    }
}

function finishProcess() {
    insertHistoryAction({
        name: m.render_audio_action_name(), //action name
        eventGroup: projectState.selected,
        snapshots: [{
            store: "eventGroupState",
            value: JSON.stringify(eventGroupState)
        }]
    });

    toast.push(m.render_audio_complete_toast(), {classes: ['toast-success']});
    eventGroupState[projectState.selected].selected = selectedEventCache;
    hideModal();
}

beginProcessing();
</script>

<div transition:fade={{duration: 100}} class="modal {modalState.selected === 'renderAudio' ? 'show d-block' : ''}" role="dialog" aria-labelledby="renderAudioTitle" tabindex="-1" id="RenderAudioModal">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title" id="renderAudioTitle">{m.render_audio_title()}</h4>
            </div>
            <div class="modal-body">
                <h5>{totalEvents ? m.render_audio_rendering_events({count: totalEvents}) : m.render_audio_rendering_no_events()}</h5>
                <p><i class="bi bi-exclamation-diamond-fill" role="img" aria-hidden="true"></i> {m.render_audio_leave_open_text()}</p>
                <div class="progress" role="progressbar" aria-label={m.render_audio_progress_aria_label()} aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100">
                    <div class="progress-bar bg-primary progress-bar-striped progress-bar-animated" style="width: {progress}%;">{progress}%</div>
                </div>
                <p class="text-center text-muted small" aria-live="polite">{statusMsg}</p>
            </div>
        </div>
    </div>
</div>
